import React, { useEffect, useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Box,
  useTheme,
  useMediaQuery,
  Slide,
  Fade,
  Grow,
  Button,
  Divider,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout,
  Settings,
  Person,
  Bookmark,
  Chat,
  AccountCircle,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '@store/index'
import { logout } from '@store/slices/authSlice'
import { toggleSidebar } from '@store/slices/appSlice'
import { useNavigate, useLocation } from 'react-router-dom'
import { AuthService } from '../../services/authService'
import { UserRole } from '../../types/index'
import { SearchBar } from '../search'
import api from '@services/api'
import { useGooglePlaces } from '@hooks/useGooglePlaces'
import CloseIcon from '@mui/icons-material/Close'

type SearchOption = {
  id: string
  label: string
  raw?: any
}

type SelectedLocation = {
  label: string
  latitude?: number
  longitude?: number
  placeId?: string
}

export const ProviderAppBar: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation() // 👈 track current route
  const dispatch = useAppDispatch()
  const [unopenedCount, setUnopenedCount] = useState(0)

  const { user } = useAppSelector(state => state.auth)
  const userId = user?.id

  // console.log('User ID:', userId)

  // const { notifications } = useAppSelector(state => state.app)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [notificationAnchor, setNotificationAnchor] =
    React.useState<null | HTMLElement>(null)

  // Search state
  const [searchLocation, setSearchLocation] = useState<string | null>('')
  const [searchInsurance, setSearchInsurance] = useState<string | null>('')
  const [searchSpecialization, setSearchSpecialization] = useState<
    string | null
  >('')
  const [notification, setNotification] = useState<any[]>([]) // You can specify a more specific type if you know the structure
  const [notificationLoading, setNotificationLoading] = useState<boolean>(false)
  const [showAllNotifications, setShowAllNotifications] =
    useState<boolean>(false)
  const NOTIFICATIONS_TO_SHOW = 10
  const [searchRadius, setSearchRadius] = useState<string | null>('')
  const [insuranceOptions, setInsuranceOptions] = useState<SearchOption[]>([])
  const [specializationOptions, setSpecializationOptions] = useState<
    SearchOption[]
  >([])
  const [insuranceLoading, setInsuranceLoading] = useState(false)
  const [specializationLoading, setSpecializationLoading] = useState(false)
  const [insuranceSearchTerm, setInsuranceSearchTerm] = useState('')
  const [specializationSearchTerm, setSpecializationSearchTerm] = useState('')
  const [selectedInsurance, setSelectedInsurance] =
    useState<SearchOption | null>(null)
  const [selectedSpecialization, setSelectedSpecialization] =
    useState<SearchOption | null>(null)
  const [locationPredictions, setLocationPredictions] = useState<any[]>([])
  const [locationSearchTerm, setLocationSearchTerm] = useState('')
  const [selectedLocationDetails, setSelectedLocationDetails] =
    useState<SelectedLocation | null>(null)
  const [isFetchingPlaceDetails, setIsFetchingPlaceDetails] = useState(false)
  const [searchButtonLoading, setSearchButtonLoading] = useState(false)
  const {
    isLoaded: isPlacesLoaded,
    isLoading: isPlacesLoading,
    error: placesError,
    autocompleteService,
    placesService,
  } = useGooglePlaces()

  const normalizeInsuranceOption = (item: any): SearchOption => {
    const label =
      item?.payer_name ||
      item?.label ||
      item?.name ||
      item?.plan_name ||
      item?.company ||
      'Insurance'
    const id =
      item?._id ||
      item?.payerid ||
      item?.payerId ||
      item?.id ||
      item?.value ||
      item?.uuid ||
      label
    return {
      id,
      label,
      raw: item,
    }
  }

  const normalizeSpecializationOption = (item: any): SearchOption => {
    const label =
      item?.name ||
      item?.label ||
      item?.title ||
      item?.specility ||
      'Specialization'
    const id =
      item?._id ||
      item?.id ||
      item?.value ||
      item?.slug ||
      item?.specilityId ||
      label
    return {
      id,
      label,
      raw: item,
    }
  }

  const fetchInsuranceOptions = async (searchTerm: string) => {
    setInsuranceLoading(true)
    try {
      const response = await api.get('/payers/all', {
        params: searchTerm ? { search: searchTerm } : undefined,
      })
      const data = response.data?.data ?? response.data ?? []
      setInsuranceOptions(data.map(normalizeInsuranceOption))
    } catch (error) {
      console.error('Error fetching insurance options:', error)
      setInsuranceOptions([])
    } finally {
      setInsuranceLoading(false)
    }
  }

  const fetchSpecializationOptions = async (searchTerm: string) => {
    setSpecializationLoading(true)
    try {
      const response = await api.get('/specilities/specility', {
        params: searchTerm ? { search: searchTerm } : undefined,
      })
      const data = response.data?.data ?? response.data ?? []
      setSpecializationOptions(data.map(normalizeSpecializationOption))
    } catch (error) {
      console.error('Error fetching specialization options:', error)
      setSpecializationOptions([])
    } finally {
      setSpecializationLoading(false)
    }
  }
  const fetchNotifications = async () => {
    setNotificationLoading(true)
    try {
      const response = await api.get('/web-notifications')
      const data = response?.data?.notifications ?? []
      // Normalize the read property - handle both 'read' and 'isRead' from API
      const normalizedData = data.map((notification: any) => ({
        ...notification,
        read:
          notification.read !== undefined
            ? notification.read
            : notification.isRead !== undefined
              ? notification.isRead
              : false,
      }))
      setNotification(normalizedData)
    } catch (error) {
      console.error('Error fetching notifications:', error)
      setNotification([])
    } finally {
      setNotificationLoading(false)
    }
  }

  const fetchUnopenedCount = async () => {
    try {
      const response = await api.get('/web-notifications/unopened-count')
      const data = response?.data?.unOpenedCount ?? 0
      setUnopenedCount(data)
    } catch (error) {
      console.error('Error fetching unopened count:', error)
      setUnopenedCount(0)
    }
  }

  // Function to mark notification as read
  const markNotificationAsRead = async (notificationId: string) => {
    try {
      const response = await api.patch(
        `/web-notifications/read/${notificationId}`
      )

      if (response?.status === 200) {
        fetchNotifications() // Reload notifications to reflect the change
        fetchUnopenedCount() // Update unopened count
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  // Function to handle notification click and navigate based on notificationType
  const handleNotificationClick = (notification: any) => {
    const { notificationType, therapistId, patientId, _id } = notification

    // Mark notification as read first
    markNotificationAsRead(_id)

    // Close notification menu
    handleNotificationMenuClose()

    // Navigate based on notificationType
    switch (notificationType) {
      case 'chat':
        // Navigate to messages with appropriate query params
        // If user is THERAPIST, navigate to messages with the patient who sent the message
        // If user is CLIENT, navigate to messages with the therapist who sent the message
        if (user?.role === UserRole.THERAPIST && patientId?._id) {
          navigate(`/messages?patientId=${patientId._id}`)
        } else if (user?.role === UserRole.CLIENT && therapistId?._id) {
          navigate(`/messages?therapistId=${therapistId._id}`)
        } else {
          // Fallback to general messages page
          navigate(
            '/messages' +
            (user?.role === UserRole.THERAPIST
              ? '?therapistId=' + user?.id
              : '') +
            (user?.role === UserRole.CLIENT ? '?patientId=' + user?.id : '')
          )
        }
        break

      case 'contract_pending':
      case 'contract':
        // Navigate to contracts page
        navigate('/therapy-contracts')
        break

      case 'job_alert':
        // Navigate to job listing page
        navigate('/job-listing')
        break

      default:
        // For other notification types, you can add more cases here
        console.log('Unhandled notification type:', notificationType)
        break
    }
  }

  const addLineBreakAfterWords = (text, wordsPerLine) => {
    const words = text.split(' ') // Split the text into individual words
    let lines = [] // Initialize an array to hold the lines

    let currentLine = '' // Track the current line as words are added

    // Loop through each word
    words.forEach(word => {
      // If adding the next word exceeds the word limit, start a new line
      if (currentLine.split(' ').length < wordsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word // Add the word to the current line
      } else {
        lines.push(currentLine) // Push the current line to the lines array
        currentLine = word // Start a new line with the current word
      }
    })

    // Push the final line if there are any leftover words
    if (currentLine) {
      lines.push(currentLine)
    }

    return lines.join('\n') // Join the lines array with newline characters to break into lines
  }

  // Format date and time from createdAt field
  const formatNotificationDate = (createdAt: string) => {
    if (!createdAt) return ''

    const date = new Date(createdAt)
    const now = new Date()

    // Get local date components for proper date comparison
    const dateYear = date.getFullYear()
    const dateMonth = date.getMonth()
    const dateDay = date.getDate()
    const nowYear = now.getFullYear()
    const nowMonth = now.getMonth()
    const nowDay = now.getDate()

    // Calculate difference in calendar days (not just milliseconds)
    const dateStartOfDay = new Date(dateYear, dateMonth, dateDay).getTime()
    const nowStartOfDay = new Date(nowYear, nowMonth, nowDay).getTime()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / 60000)
    const diffInCalendarDays = Math.floor(
      (nowStartOfDay - dateStartOfDay) / 86400000
    )

    // Format for same day: "Today at 2:30 PM"
    if (diffInCalendarDays === 0) {
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      if (diffInMinutes < 1) {
        return 'Just now'
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`
      } else {
        return `Today at ${timeStr}`
      }
    }

    // Format for yesterday: "Yesterday at 2:30 PM"
    if (diffInCalendarDays === 1) {
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      return `Yesterday at ${timeStr}`
    }

    // Format for this week: "Monday at 2:30 PM"
    if (diffInCalendarDays < 7) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      return `${dayName} at ${timeStr}`
    }

    // Format for older dates: "Jan 15, 2026 at 2:30 PM"
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: dateYear !== nowYear ? 'numeric' : undefined,
    })
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    return `${dateStr} at ${timeStr}`
  }

  useEffect(() => {
    fetchNotifications()
    fetchUnopenedCount()

    // Set up polling for live unopened count updates (every 30 seconds)
    const countInterval = setInterval(() => {
      fetchUnopenedCount()
    }, 30000) // Poll every 30 seconds

    // Cleanup interval on unmount
    return () => {
      clearInterval(countInterval)
    }
  }, [])

  // Also refresh notifications and count when notification menu is opened
  useEffect(() => {
    if (notificationAnchor) {
      fetchNotifications()
      fetchUnopenedCount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationAnchor])

  useEffect(() => {
    const trimmed = insuranceSearchTerm.trim()
    if (!trimmed) {
      setInsuranceOptions([])
      return
    }
    const handler = setTimeout(() => {
      fetchInsuranceOptions(trimmed)
    }, 400)

    return () => clearTimeout(handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceSearchTerm])

  useEffect(() => {
    const handler = setTimeout(
      () => {
        fetchSpecializationOptions(specializationSearchTerm)
      },
      specializationSearchTerm ? 400 : 0
    )

    return () => clearTimeout(handler)
    // eslint-disable-next-line react-hooks-exhaustive-deps
  }, [specializationSearchTerm])

  useEffect(() => {
    if (
      !autocompleteService ||
      !locationSearchTerm ||
      !locationSearchTerm.trim()
    ) {
      setLocationPredictions([])
      return
    }

    let active = true
    autocompleteService.getPlacePredictions(
      {
        input: locationSearchTerm,
        types: ['geocode'],
      },
      (predictions, status) => {
        if (active) {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            console.log('Location predictions received:', predictions)
            setLocationPredictions(predictions)
          } else {
            console.log(
              'Location predictions status:',
              status,
              'predictions:',
              predictions
            )
            setLocationPredictions([])
          }
        }
      }
    )

    return () => {
      active = false
    }
  }, [autocompleteService, locationSearchTerm])

  useEffect(() => {
    if (placesError) {
      console.error('Google Places failed to initialize:', placesError)
    }
  }, [placesError])

  const handleLocationOptionSelect = (option: any) => {
    if (!option) {
      setSelectedLocationDetails(null)
      // Clear radius when location is cleared
      setSearchRadius(null)
      return
    }

    const description =
      typeof option === 'string'
        ? option
        : option.formatted_address || option.description || option.label || ''

    setSelectedLocationDetails(() => ({
      label: description,
      latitude: undefined,
      longitude: undefined,
      placeId:
        typeof option === 'string'
          ? undefined
          : (option.place_id ?? option.placeId),
    }))

    if (
      typeof option !== 'string' &&
      option.place_id &&
      placesService &&
      isPlacesLoaded
    ) {
      setIsFetchingPlaceDetails(true)
      placesService.getDetails(
        {
          placeId: option.place_id,
          fields: ['geometry', 'formatted_address', 'address_components'],
        },
        place => {
          setIsFetchingPlaceDetails(false)
          if (place && place.geometry?.location) {
            const latitude = place.geometry.location.lat()
            const longitude = place.geometry.location.lng()
            const components = place.address_components || []
            const findComponent = (types: string[]) =>
              components.find(component =>
                types.some(type => component.types.includes(type))
              )
            const countryComponent = findComponent(['country'])
            const isUSA =
              countryComponent?.short_name?.toLowerCase() === 'us' ||
              countryComponent?.long_name?.toLowerCase() === 'united states'
            // if (!isUSA) {
            //   setSelectedLocationDetails(null)
            //   // setSearchLocation('')
            //   // Clear radius when location is invalid
            //   setSearchRadius(null)
            //   return
            // }

            // Use the description from the prediction or formatted_address from place details
            const finalLabel = description || place.formatted_address || ''

            setSelectedLocationDetails({
              label: finalLabel,
              latitude,
              longitude,
              placeId: option.place_id,
            })
            setSearchLocation(finalLabel)
          }
        }
      )
      return
    }

    setSearchLocation(description)
  }

  const findOptionByLabel = (options: SearchOption[], label: string) =>
    options.find(
      option => option.label.toLowerCase() === label.toLowerCase().trim()
    ) || null

  const handleInsuranceOptionSelect = (option: any) => {
    if (!option) {
      setSelectedInsurance(null)
      return
    }
    if (typeof option === 'string') {
      const matched = findOptionByLabel(insuranceOptions, option)
      setSelectedInsurance(matched)
      return
    }
    setSelectedInsurance(normalizeInsuranceOption(option))
  }

  const handleSpecializationOptionSelect = (option: any) => {
    if (!option) {
      setSelectedSpecialization(null)
      return
    }
    if (typeof option === 'string') {
      const matched = findOptionByLabel(specializationOptions, option)
      setSelectedSpecialization(matched)
      return
    }
    setSelectedSpecialization(normalizeSpecializationOption(option))
  }

  const handleLocationInputChange = (value: string) => {
    setLocationSearchTerm(value)
    if (!value) {
      setSelectedLocationDetails(null)
      // Clear radius when location is cleared
      setSearchRadius(null)
    }
  }

  const handleLocationChange = (value: string | null) => {
    setSearchLocation(value)
    // Clear radius when location is cleared
    if (!value || !value.trim()) {
      setSearchRadius(null)
    }
  }

  const unreadNotifications = notification.filter(n => !n.read).length

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget)
  }

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null)
    setShowAllNotifications(false) // Reset to show only top 10 when menu closes
  }

  const handleLogout = async () => {
    try {
      // Call therapist logout API if user is authenticated and is a therapist
      await AuthService.therapistLogout()
    } catch (error) {
      console.error('Logout failed:', error)
      // Even if API call fails, proceed with local logout
    } finally {
      dispatch(logout())
      handleProfileMenuClose()
      handleNotificationMenuClose()
      navigate('/provider-login')
    }
  }

  const handleProfile = () => {
    // navigate('/profile')
    navigate('/account-settings', {
      state: { from: location.pathname === '/dashboard' ? 'dashboard' : undefined }
    })
    handleProfileMenuClose()
  }
  console.log("searchRadius", searchRadius)
  const handleSearch = async () => {
    if (searchButtonLoading) return

    const params = new URLSearchParams()
    if (searchLocation?.trim()) {
      params.set('location', searchLocation.trim())
    }
    if (selectedLocationDetails?.latitude) {
      params.set('latitude', selectedLocationDetails.latitude.toString())
    }
    if (selectedLocationDetails?.longitude) {
      params.set('longitude', selectedLocationDetails.longitude.toString())
    }
    //radius


    const result = searchRadius?.replace(" miles", "").trim(); // "10"
    if (searchRadius?.trim()) {
      params.set('radius', result)
    }
    if (selectedSpecialization?.id) {
      params.set('specialization', selectedSpecialization.id)
      params.set('specializationName', selectedSpecialization.label)
    } else if (searchSpecialization?.trim()) {
      params.set('specializationName', searchSpecialization.trim())
    }
    if (selectedInsurance?.id) {
      params.set('insurance', selectedInsurance.id)
      params.set('insuranceName', selectedInsurance.label)
    } else if (searchInsurance?.trim()) {
      params.set('insuranceName', searchInsurance.trim())
    }
    // Only include radius if location coordinates are available
    if (
      searchRadius?.trim() &&
      selectedLocationDetails?.latitude &&
      selectedLocationDetails?.longitude
    ) {
      const radiusValue = parseFloat(searchRadius.trim())
      if (!isNaN(radiusValue)) {
        params.set('radius', radiusValue.toString())
      }
    }
    const tabValue = 'jobs'
    params.set('tab', tabValue)
    params.set('page', '1')
    params.set('limit', '10')

    const requestParams: Record<string, string> = {}
    const excludeFromApi = new Set(['insuranceName', 'specializationName'])
    params.forEach((value, key) => {
      if (value && !excludeFromApi.has(key)) {
        requestParams[key] = value
      }
    })

    setSearchButtonLoading(true)
    try {
      const response = await api.get('/patient-job/search-jobs-patients', {
        params: requestParams,
      })

      let listings: any[] = []
      if (response.data) {
        if (Array.isArray(response.data)) {
          listings = response.data
        } else if (Array.isArray(response.data.data)) {
          listings = response.data.data
        } else if (
          response.data.data &&
          Array.isArray(response.data.data.data)
        ) {
          listings = response.data.data.data
        } else {
          listings = response.data?.data ?? []
        }
      }

      const totalRecordsValue =
        typeof response?.data?.totalRecords === 'number'
          ? response.data.totalRecords
          : typeof response?.data?.data?.totalRecords === 'number'
            ? response.data.data.totalRecords
            : typeof response?.data?.meta?.totalRecords === 'number'
              ? response.data.meta.totalRecords
              : listings.length

      navigate(`/job-listing?${params.toString()}`, {
        state: {
          searchResults: listings,
          totalRecords: totalRecordsValue,
        },
      })
    } catch (error) {
      console.error('Error searching jobs/patients:', error)
    } finally {
      setSearchButtonLoading(false)
    }
  }

  // 👇 Auto-close both menus when route changes
  React.useEffect(() => {
    handleProfileMenuClose()
    handleNotificationMenuClose()
  }, [location.pathname])

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'white',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        height: '80px',
        flexShrink: 0,
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          py: 1,
          height: '100%',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Left Section - Logo */}
        <Slide direction="right" in timeout={1000}>
          <Box
            component="img"
            src="/logo.svg"
            alt="PillarThera Logo"
            sx={{
              height: { xs: 32, sm: 60 },
              width: 'auto',
              cursor: 'pointer',
              transition: 'opacity 0.3s ease',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          />
        </Slide>

        {/* Center Section - Search Bar */}
        <Fade in timeout={800} style={{ transitionDelay: '200ms' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              gap: 2,
              maxWidth: { xs: '50%' },
              mx: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'flex-start',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.main',
                  fontSize: { sm: '0.9rem', md: '1.2rem' },
                  fontWeight: 800,
                }}
              >
                Search
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.main',
                  mb: 1,
                  fontSize: { sm: '0.9rem', md: '1.2rem' },
                  fontWeight: 800,
                }}
              >
                Jobs/Patients
              </Typography>
            </Box>
            {/* Search Bar */}
            <Grow in timeout={1000} style={{ transitionDelay: '1300ms' }}>
              <Box width="100%" sx={{ maxWidth: { sm: '100%' } }}>
                <SearchBar
                  location={searchLocation}
                  insurance={searchInsurance}
                  specialization={searchSpecialization}
                  radius={searchRadius}
                  locationOptions={locationPredictions}
                  insuranceOptions={insuranceOptions}
                  specializationOptions={specializationOptions}
                  locationLoading={isPlacesLoading || isFetchingPlaceDetails}
                  insuranceLoading={insuranceLoading}
                  specializationLoading={specializationLoading}
                  onLocationChange={handleLocationChange}
                  onInsuranceChange={setSearchInsurance}
                  onSpecializationChange={setSearchSpecialization}
                  onRadiusChange={setSearchRadius}
                  onLocationSelect={handleLocationOptionSelect}
                  onInsuranceSelect={handleInsuranceOptionSelect}
                  onSpecializationSelect={handleSpecializationOptionSelect}
                  onLocationInputChange={handleLocationInputChange}
                  onInsuranceInputChange={setInsuranceSearchTerm}
                  onSpecializationInputChange={setSpecializationSearchTerm}
                  onSearch={handleSearch}
                  isSearchLoading={searchButtonLoading}
                  showSearchButton={true}
                  compact={true}
                  sx={{
                    height: '60px',
                  }}
                />
              </Box>
            </Grow>
          </Box>
        </Fade>

        {/* Right Section - Icons */}
        <Slide
          direction="left"
          in
          timeout={1000}
          style={{ transitionDelay: '400ms' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={() => dispatch(toggleSidebar())}
                sx={{
                  color: 'grey.600',
                  mr: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'primary.main',
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Bookmark Icon */}
            <IconButton
              onClick={() => navigate('/saved-jobs')}
              sx={{
                color: 'grey.600',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'primary.main',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <Bookmark />
            </IconButton>

            {/* Chat Icon */}
            <IconButton
              onClick={() =>
                navigate(
                  '/messages' +
                  (user?.role === UserRole.THERAPIST
                    ? '?therapistId=' + user?.id
                    : '') +
                  (user?.role === UserRole.CLIENT
                    ? '?patientId=' + user?.id
                    : '')
                )
              }
              sx={{
                color: 'grey.600',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'primary.main',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <Chat />
            </IconButton>

            {/* Notifications */}
            <IconButton
              onClick={handleNotificationMenuOpen}
              sx={{
                color: 'grey.600',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'primary.main',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <Badge badgeContent={notification.filter(n => !n.read).length} color="info">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* Profile Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* User Info */}
              {/* <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  textAlign: 'right',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'grey.800',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                  }}
                >
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'grey.600',
                    fontSize: '0.75rem',
                    lineHeight: 1,
                  }}
                >
                  {user?.email}
                </Typography>
              </Box> */}

              {/* Profile Avatar */}
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{
                  color: 'grey.600',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'primary.main',
                    transform: 'scale(1.05)',
                  },
                  p: 0.5,
                }}
              >
                {user?.avatar ? (
                  <Avatar
                    src={user.avatar}
                    alt={`${user.firstName} ${user.lastName}`}
                    sx={{
                      width: 50,
                      height: 50,
                      border: '2px solid',
                      borderColor: 'grey.200',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: 'primary.main',
                      border: '2px solid',
                      borderColor: 'grey.200',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Person sx={{ fontSize: 24 }} />
                  </Avatar>
                )}
              </IconButton>
            </Box>
          </Box>
        </Slide>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            minWidth: 220,
          },
        }}
      >
        {/* User Info Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {user?.avatar ? (
              <Avatar
                src={user.avatar}
                alt={`${user.firstName} ${user.lastName}`}
                sx={{ width: 40, height: 40 }}
              />
            ) : (
              <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                <Person />
              </Avatar>
            )}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'grey.800' }}
              >
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'grey.600' }}>
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        <MenuItem onClick={handleProfile}>
          <Settings sx={{ mr: 2, fontSize: '1.2rem' }} />
          Account settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 2, fontSize: '1.2rem' }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 500,
            maxHeight: 600,
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'grey.800',
            }}
          >
            Notifications
          </Typography>
        </Box>

        {/* Notifications List */}
        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: 450,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(0,0,0,0.3)',
              },
            },
          }}
        >
          {notificationLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Loading notifications...
              </Typography>
            </Box>
          ) : notification?.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <>
              {(showAllNotifications
                ? notification
                : notification.slice(0, NOTIFICATIONS_TO_SHOW)
              ).map((notificationItem, index) => (
                <MenuItem
                  key={notificationItem._id}
                  onClick={() => {
                    handleNotificationClick(notificationItem)
                  }}
                  sx={{
                    cursor: 'pointer',
                    py: 0,
                    px: 0,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: notificationItem.read
                        ? 'action.hover'
                        : 'rgba(25, 118, 210, 0.08)',
                    },
                    borderBottom:
                      index <
                        (showAllNotifications
                          ? notification.length - 1
                          : Math.min(notification.length, NOTIFICATIONS_TO_SHOW) -
                          1)
                        ? '1px solid'
                        : 'none',
                    borderColor: 'grey.200',
                    // Left border indicator for unread notifications
                    borderLeft: notificationItem.read ? 'none' : '4px solid',
                    borderLeftColor: notificationItem.read
                      ? 'transparent'
                      : 'primary.main',
                    bgcolor: notificationItem.read
                      ? 'transparent'
                      : 'rgba(25, 118, 210, 0.05)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      p: 2,
                      position: 'relative',
                    }}
                  >
                    {/* Unread indicator dot */}
                    {!notificationItem.read && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                        }}
                      />
                    )}
                    {/* Notification Header with Title and Timestamp */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: notificationItem.read ? 500 : 700,
                          color: notificationItem.read
                            ? 'grey.600'
                            : 'primary.main',
                          flex: 1,
                          pr: 1,
                        }}
                      >
                        {notificationItem.notificationTitle}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          color: notificationItem.read
                            ? 'grey.500'
                            : 'grey.600',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          fontWeight: notificationItem.read ? 400 : 500,
                        }}
                      >
                        {formatNotificationDate(notificationItem.createdAt)}
                      </Typography>
                    </Box>

                    {/* Notification Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        color: notificationItem.read ? 'grey.500' : 'grey.800',
                        lineHeight: 1.5,
                        mt: 0.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontWeight: notificationItem.read ? 400 : 500,
                      }}
                    >
                      {notificationItem?.notificationDescription}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </>
          )}
        </Box>

        {/* Show More/Less Button */}
        {notification?.length > NOTIFICATIONS_TO_SHOW && (
          <>
            <Divider />
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={() => setShowAllNotifications(!showAllNotifications)}
                endIcon={showAllNotifications ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  textTransform: 'none',
                  color: 'primary.main',
                  fontSize: '0.875rem',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {showAllNotifications
                  ? 'Show Less'
                  : `Show More (${notification.length - NOTIFICATIONS_TO_SHOW} more)`}
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </AppBar>
  )
}

export default ProviderAppBar
